import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pagination, message } from 'antd';
import { CalendarDays, ChevronRight, Download, LayoutDashboard, Mail, Phone, Search, User } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import Spinner from '@/components/Spinner';
import {
    DocumentsTab,
    InvestmentTab,
    MembersCommissionTab,
    TransactionsTab,
} from '@/components/Investors';
import { CommissionRecord } from '@/components/Investors/MembersCommissionTab';
import { Transaction } from '@/components/Investors/TransactionsTab';
import { getCmoDetail } from '@/api/Admin/cmo';
import { fetchUserData } from '@/api/Admin/User';
import { userInvestmentPackageStats } from '@/api/Admin/userPackage';
import { InvestmentPackage } from '@/api/Admin/type';
import { formatCurrency } from '@/lib/format';
import dayjs from 'dayjs';
import { ActiveTab, DirectorStatus, getPackageProgress, Member, MemberPackageStatus } from './data';

interface TabConfig {
    id: ActiveTab;
    label: string;
}

interface GdkdProfile {
    id: string;
    code: string;
    fullname: string;
    email: string;
    phone: string;
    avatar_url?: string;
    gender: number | string | null;
    birthday: string | null;
    job_title: string | null;
    identifier: string;
    referral_code: string;
    status: 'Active' | 'Inactive';
}

type ExportColumnKey =
    | 'memberName'
    | 'memberId'
    | 'email'
    | 'phone'
    | 'assignee'
    | 'packageName'
    | 'packageStatus'
    | 'transferDate'
    | 'duration'
    | 'totalInvestment'
    | 'memberTotalInvestment'
    | 'isTransferred';

const EXPORT_COLUMNS: { key: ExportColumnKey; label: string }[] = [
    { key: 'memberName', label: 'Thành viên' },
    { key: 'memberId', label: 'Mã thành viên' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Số điện thoại' },
    { key: 'assignee', label: 'Người phụ trách hiện tại' },
    { key: 'packageName', label: 'Gói đầu tư' },
    { key: 'packageStatus', label: 'Trạng thái gói' },
    { key: 'transferDate', label: 'Ngày nhận' },
    { key: 'duration', label: 'Thời hạn (tháng)' },
    { key: 'totalInvestment', label: 'Đầu tư theo gói' },
    { key: 'memberTotalInvestment', label: 'Tổng đầu tư thành viên' },
    { key: 'isTransferred', label: 'Chuyển giao' },
];

const DEFAULT_EXPORT_COLUMN_LABELS = EXPORT_COLUMNS.reduce((acc, column) => {
    acc[column.key] = column.label;
    return acc;
}, {} as Record<ExportColumnKey, string>);

const DEFAULT_SELECTED_EXPORT_COLUMNS = EXPORT_COLUMNS.map((column) => column.key);

function getExportColumnValue(member: Member, pkg: Member['packages'][number], columnKey: ExportColumnKey) {
    switch (columnKey) {
        case 'memberName':
            return member.name;
        case 'memberId':
            return member.idLabel;
        case 'email':
            return member.email;
        case 'phone':
            return member.phone;
        case 'assignee':
            return pkg.assignee;
        case 'packageName':
            return pkg.name;
        case 'packageStatus':
            return pkg.status;
        case 'transferDate':
            return pkg.transferDate;
        case 'duration':
            return pkg.duration;
        case 'totalInvestment':
            return pkg.totalInvestment === '-' ? '-' : formatCurrency(pkg.totalInvestment);
        case 'memberTotalInvestment':
            return member.total === '-' ? '-' : formatCurrency(member.total);
        case 'isTransferred':
            return pkg.isTransferred ? 'Có' : 'Không';
        default:
            return '';
    }
}

function mapUserProfile(userInfo: Awaited<ReturnType<typeof fetchUserData>>): GdkdProfile {
    return {
        id: String(userInfo.id),
        code: String(userInfo.id),
        fullname: userInfo.fullname,
        email: userInfo.email,
        phone: userInfo.phone || '',
        gender: userInfo.gender,
        birthday: userInfo.birthday,
        job_title: userInfo.job_title,
        identifier: userInfo.cccd_number || userInfo.cccd || '',
        referral_code: userInfo.referral_code || '',
        status: userInfo.is_active === 1 ? 'Active' : 'Inactive',
    };
}

function getReferralPackageStatus(remainingMonths: number): MemberPackageStatus {
    if (remainingMonths <= 0) {
        return 'Hết thời gian ĐT';
    }

    return 'Hoạt động';
}

function resolveReferralStatus(status: string | undefined, remainingMonths: number): MemberPackageStatus {
    return status ? normalizeMemberStatus(status) : getReferralPackageStatus(remainingMonths);
}

function normalizeMemberStatus(status?: string): MemberPackageStatus {
    const normalizedStatus = status?.trim().toLowerCase();

    if (normalizedStatus === 'active' || normalizedStatus === 'hoạt động') {
        return 'Hoạt động';
    }
    if (normalizedStatus === 'inactive' || normalizedStatus === 'ngừng hoạt động') {
        return 'Ngừng hoạt động';
    }
    if (normalizedStatus === 'hết thời gian đầu tư' || normalizedStatus === 'expired') {
        return 'Hết thời gian ĐT';
    }

    return 'Ngừng hoạt động';
}

function mapMemberStatusFilterToApiStatus(status: 'Tất cả trạng thái' | MemberPackageStatus): 'active' | 'inactive' | 'transfer' | 'expired' | '' {
    switch (status) {
        case 'Hoạt động':
            return 'active';
        case 'Ngừng hoạt động':
            return 'inactive';
        case 'Hết thời gian ĐT':
            return 'expired';
        default:
            return '';
    }
}

function mapTransferFilterToApiValue(filter: 'all' | 'is_transfer' | 'not_is_transfer'): boolean | undefined {
    switch (filter) {
        case 'is_transfer':
            return true;
        case 'not_is_transfer':
            return false;
        default:
            return undefined;
    }
}

function StatusBadge({ status }: { status: DirectorStatus | MemberPackageStatus }) {
    const { t } = useTranslation();
    let bgColor = 'bg-gray-200';
    let textColor = 'text-gray-600';

    if (status === 'Hoạt động') {
        bgColor = 'bg-green-100 dark:bg-green-900/30';
        textColor = 'text-green-700 dark:text-green-400';
    } else if (status === 'Ngừng hoạt động') {
        bgColor = 'bg-red-100 dark:bg-red-900/30';
        textColor = 'text-red-700 dark:text-red-400';
    }

    const displayLabel =
        status === 'Hoạt động' ? t('gdkd.statusActive') :
        status === 'Ngừng hoạt động' ? t('gdkd.statusInactive') :
        status === 'Hết thời gian ĐT' ? t('gdkd.detail.members.statusExpired') :
        status;

    return <span className={`whitespace-nowrap rounded-md px-2 py-1 text-center text-[11px] font-semibold ${bgColor} ${textColor}`}>{displayLabel}</span>;
}

export default function GdkdDetailPage() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState<ActiveTab>('member');
    const [memberStatusFilter, setMemberStatusFilter] = useState<'Tất cả trạng thái' | MemberPackageStatus>('Tất cả trạng thái');
    const [transferFilter, setTransferFilter] = useState<'all' | 'is_transfer' | 'not_is_transfer'>('all');
    const [memberSearchTerm, setMemberSearchTerm] = useState('');
    const [debouncedMemberSearchTerm, setDebouncedMemberSearchTerm] = useState('');
    const [investor, setInvestor] = useState<GdkdProfile | null>(null);
    const [members, setMembers] = useState<Member[]>([]);
    const [investmentStats, setInvestmentStats] = useState<{
        totalPackages: number;
        totalInvestment: number;
        totalProfit: number;
        total_overdue: number;
    } | undefined>(undefined);
    const [investments, setInvestments] = useState<InvestmentPackage[]>([]);
    const [transactions, setTransactions] = useState<Transaction[] | undefined>(undefined);
    const [transactionStats, setTransactionStats] = useState<{
        totalIn: number;
        totalOut: number;
        totalTransactions: number;
    } | undefined>(undefined);
    const [commissionData, setCommissionData] = useState<{
        stats: { totalCommission: number; totalPaid: number; totalPending: number; totalRejected: number };
        records: CommissionRecord[];
    } | undefined>(undefined);
    const [docs, setDocs] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [transPage, setTransPage] = useState(1);
    const [transTotal, setTransTotal] = useState(0);
    const [commPage, setCommPage] = useState(1);
    const [commTotal, setCommTotal] = useState(0);
    const [docPage, setDocPage] = useState(1);
    const [docTotal, setDocTotal] = useState(0);
    const [memberPage, setMemberPage] = useState(1);
    const [memberTotal, setMemberTotal] = useState(0);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [selectedExportColumns, setSelectedExportColumns] = useState<ExportColumnKey[]>(DEFAULT_SELECTED_EXPORT_COLUMNS);
    const [exportColumnLabels, setExportColumnLabels] = useState<Record<ExportColumnKey, string>>(DEFAULT_EXPORT_COLUMN_LABELS);

    const memberPageSize = 10;

    const profile = investor;
    const memberStatusApiFilter = mapMemberStatusFilterToApiStatus(memberStatusFilter);
    const memberTransferApiFilter = mapTransferFilterToApiValue(transferFilter);

    const filteredMembers = useMemo(() => {
        return members.filter((member) => {
            const matchesStatus = memberStatusFilter === 'Tất cả trạng thái'
                ? true
                : member.packages.some((pkg) => pkg.status === memberStatusFilter) || member.status === memberStatusFilter;
            const matchesTransfer = transferFilter === 'all'
                ? true
                : transferFilter === 'is_transfer'
                    ? member.packages.some((pkg) => pkg.isTransferred)
                    : member.packages.every((pkg) => !pkg.isTransferred);

            return matchesStatus && matchesTransfer;
        });
    }, [memberStatusFilter, members, transferFilter]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedMemberSearchTerm(memberSearchTerm.trim());
        }, 1000);

        return () => window.clearTimeout(timeoutId);
    }, [memberSearchTerm]);

    useEffect(() => {
        setMemberPage(1);
    }, [debouncedMemberSearchTerm, memberStatusFilter, transferFilter]);

    const handleToggleExportColumn = (columnKey: ExportColumnKey) => {
        setSelectedExportColumns((currentColumns) => (
            currentColumns.includes(columnKey)
                ? currentColumns.filter((currentColumn) => currentColumn !== columnKey)
                : [...currentColumns, columnKey]
        ));
    };

    const handleExportColumnLabelChange = (columnKey: ExportColumnKey, value: string) => {
        setExportColumnLabels((currentLabels) => ({
            ...currentLabels,
            [columnKey]: value,
        }));
    };

    const handleResetExportColumnLabels = () => {
        setExportColumnLabels(DEFAULT_EXPORT_COLUMN_LABELS);
    };

    const handleExportMembersExcel = () => {
        if (selectedExportColumns.length === 0) {
            message.warning(t('gdkd.detail.exportModal.noColumnsWarning'));
            return;
        }

        const exportItems = filteredMembers.flatMap((member) => member.packages.map((pkg) => ({ member, pkg })));

        if (exportItems.length === 0) {
            message.warning(t('gdkd.detail.exportModal.noDataWarning'));
            return;
        }

        setExporting(true);

        try {
            const rows = exportItems.map(({ member, pkg }) => (
                selectedExportColumns.reduce((acc, columnKey) => {
                    const headerName = exportColumnLabels[columnKey].trim() || DEFAULT_EXPORT_COLUMN_LABELS[columnKey];
                    acc[headerName] = getExportColumnValue(member, pkg, columnKey);
                    return acc;
                }, {} as Record<string, string | number>)
            ));

            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Thanh vien GDKD');
            XLSX.writeFile(workbook, `gdkd-members-${profile?.id || 'export'}-${dayjs().format('YYYYMMDD-HHmmss')}.xlsx`);
            setIsExportModalOpen(false);
            message.success(t('gdkd.detail.exportModal.exportSuccess'));
        } catch (error) {
            console.error('Error exporting GDKD members:', error);
            message.error(t('gdkd.detail.exportModal.exportFailed'));
        } finally {
            setExporting(false);
        }
    };

    useEffect(() => {
        const fetchGdkdDetail = async () => {
            if (!id) {
                return;
            }

            setLoading(true);
            try {
                const cmoId = Number(id);
                const [response, cmoDetail, userInfo] = await Promise.all([
                    userInvestmentPackageStats(id, 1, transPage, commPage, docPage),
                    Number.isNaN(cmoId) ? Promise.resolve(null) : getCmoDetail(cmoId, memberPage, memberPageSize, debouncedMemberSearchTerm, memberStatusApiFilter, memberTransferApiFilter),
                    fetchUserData(id),
                ]);

                setInvestor(mapUserProfile(userInfo));

                if (response.data && response.status === true) {
                    if (response.data.investment) {
                        setInvestmentStats({
                            totalPackages: response.data.investment.total_packages,
                            totalInvestment: response.data.investment.total_invested,
                            totalProfit: response.data.investment.total_paid_profit,
                            total_overdue: response.data.investment.total_overdue || 0,
                        });

                        if (response.data.investment.history) {
                            const historyData = Array.isArray(response.data.investment.history)
                                ? response.data.investment.history
                                : ((response.data.investment.history as any)?.data || []);

                            if (Array.isArray(historyData)) {
                                const mappedInvestments: InvestmentPackage[] = historyData.map((hist: any, index: number) => {
                                    let profits: any[] = [];
                                    if (Array.isArray(hist.profits)) {
                                        profits = hist.profits;
                                    } else if (hist.profits?.data && Array.isArray(hist.profits.data)) {
                                        profits = hist.profits.data;
                                    } else if (hist.profits && typeof hist.profits === 'object') {
                                        const values = Object.values(hist.profits);
                                        const objectValues = values.filter((value: any) => value && typeof value === 'object' && !Array.isArray(value));
                                        if (objectValues.length > 0) {
                                            profits = objectValues;
                                        }
                                    }

                                    const invested = Number(hist.total_invested || 0);
                                    const userPackageId = Number(hist.user_package_id ?? hist.id ?? index + 1);

                                    return {
                                        id: userPackageId,
                                        package_id: Number(hist.package_id || 0),
                                        package_name: hist.package_name,
                                        status_label: hist.status,
                                        status_code: hist.status,
                                        buy_date: hist.created_at,
                                        total_invested: invested,
                                        total_months: hist.total_months,
                                        monthly_profit: hist.monthly_profit,
                                        interest_rate: '0',
                                        progress: hist.progress,
                                        payment_history: hist.payments || [],
                                        referrer_id: hist.referrer_id ? hist.referrer_id : undefined,
                                        profits_history: profits.map((profit: any) => ({
                                            ...profit,
                                            period: profit.period_index,
                                            dueDate: profit.due_date,
                                            paidDate: profit.paid_at,
                                            amount: profit.net_amount,
                                        })),
                                    };
                                });

                                setInvestments(mappedInvestments);
                            }
                        }
                    }

                    if (response.data.transaction && response.data.transaction.logs) {
                        const logsData = response.data.transaction.logs.data || [];
                        const total = response.data.transaction.logs.total || 0;
                        setTransTotal(total);
                        setTransactionStats({
                            totalIn: response.data.transaction.money_in,
                            totalOut: response.data.transaction.money_out,
                            totalTransactions: response.data.transaction.count,
                        });

                        const mappedTransactions: Transaction[] = logsData.map((log) => ({
                            id: String(log.id),
                            code: log.payment_code,
                            time: dayjs(log.created_at).format('YYYY-MM-DD HH:mm:ss'),
                            type: log.transaction_type as Transaction['type'],
                            amount: Number(log.amount),
                            status: log.status === 'success' || log.status === 'succeed' ? 'completed' : (log.status === 'pending' ? 'pending' : 'failed'),
                        }));
                        setTransactions(mappedTransactions);
                    }

                    if (response.data.commission) {
                        const commission = response.data.commission;
                        const logsData = commission.logs?.data || [];
                        const total = commission.logs?.total || 0;
                        setCommTotal(total);

                        const records: CommissionRecord[] = logsData.map((log) => ({
                            id: String(log.id),
                            code: log.user_id.toString(),
                            partnerName: log.user_info?.fullname || 'N/A',
                            referrer: response.data?.user_info.fullname || 'N/A',
                            contractCode: log.package_name_snapshot,
                            investAmount: Number(log.price_snapshot),
                            commissionRate: Number(log.commission_rate_snapshot),
                            commissionAmount: Number(log.received_amount),
                            status: log.status,
                        }));

                        setCommissionData({
                            stats: {
                                totalCommission: commission.total_commission || 0,
                                totalPaid: commission.paid_commission || 0,
                                totalPending: commission.pending_commission || 0,
                                totalRejected: 0,
                            },
                            records,
                        });
                    } else {
                        setCommissionData({
                            stats: { totalCommission: 0, totalPaid: 0, totalPending: 0, totalRejected: 0 },
                            records: [],
                        });
                    }

                    if (response.data.documents && response.data.documents.data) {
                        setDocs(response.data.documents.data);
                        setDocTotal(response.data.documents.total || 0);
                    }
                }
                // Mapping referral members from CMO detail response
                if (cmoDetail?.data && Array.isArray(cmoDetail.data)) {
                    const mappedMembers: Member[] = cmoDetail.data.map((cmo, cmoIndex) => {
                        const fallbackStatus = resolveReferralStatus(cmo.status, 0);

                        const packages = Array.isArray(cmo.referral_detail)
                            ? cmo.referral_detail.map((referral) => {
                                const totalMonths = Number(referral.package_info?.total_month || 0);
                                const remainingMonths = Number(referral.package_info?.remaining_month || 0);
                                const packageStatus = resolveReferralStatus(referral.package_info?.status, remainingMonths);

                                return {
                                    name: referral.package_info?.package_name ? `Gói: ${referral.package_info.package_name}` : 'Gói: -',
                                    qty: 1,
                                    assignee: referral.name || cmo.user_name || 'N/A',
                                    transferDate: referral.active_date ? dayjs(referral.active_date).format('DD/MM/YYYY') : '-',
                                    totalInvestment: String(Number(referral.total_investment || 0)),
                                    isMine: true,
                                    status: packageStatus,
                                    isTransferred: Boolean(referral.package_info?.transfer_type),
                                    startDate: referral.active_date || '',
                                    duration: totalMonths,
                                };
                            })
                            : [];
                        const totalInvestment = packages.reduce((sum, pkg) => sum + Number(pkg.totalInvestment || 0), 0);

                        return {
                            name: cmo.user_name || `Thành viên ${(memberPage - 1) * memberPageSize + cmoIndex + 1}`,
                            idLabel: cmo.user_id ? `ID: ${cmo.user_id}` : `ID: ${(memberPage - 1) * memberPageSize + cmoIndex + 1}`,
                            email: cmo.email && cmo.email !== 'N/A' ? cmo.email : '-',
                            phone: cmo.sdt && cmo.sdt !== 'N/A' ? cmo.sdt : '-',
                            status: fallbackStatus,
                            packages: packages.length > 0 ? packages : [
                                {
                                    name: 'Gói: -',
                                    qty: 0,
                                    assignee: cmo.user_name || 'N/A',
                                    transferDate: '-',
                                    totalInvestment: '-',
                                    status: fallbackStatus,
                                    isMine: true,
                                    isTransferred: false,
                                    startDate: '',
                                    duration: 0,
                                },
                            ],
                            total: String(totalInvestment),
                        };
                    });

                    setMembers(mappedMembers);
                    setMemberTotal(Number(cmoDetail.meta?.total || 0));
                } else {
                    setMembers([]);
                    setMemberTotal(0);
                }
            } catch (error) {
                console.error('Error fetching GDKD detail:', error);
                setMembers([]);
                setMemberTotal(0);
            } finally {
                setLoading(false);
            }
        };

        fetchGdkdDetail();
    }, [id, transPage, commPage, docPage, memberPage, debouncedMemberSearchTerm, memberStatusApiFilter, memberTransferApiFilter]);

    const tabs: TabConfig[] = [
        { id: 'member', label: t('gdkd.detail.tabs.member') },
        { id: 'investment', label: t('gdkd.detail.tabs.investment') },
        { id: 'transactions', label: t('gdkd.detail.tabs.transactions') },
        { id: 'commission', label: t('gdkd.detail.tabs.commission') },
        { id: 'contracts', label: t('gdkd.detail.tabs.contracts') },
    ];

    const renderMemberTab = () => (
        <div>
            <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between"><span className="font-medium text-gray-600 dark:text-neutral-300">{t('gdkd.detail.members.total', { count: memberTotal })}</span><button onClick={() => setIsExportModalOpen(true)} className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700"><Download size={16} />{t('gdkd.detail.members.exportExcel')}</button></div>
            <div className="mb-6 flex flex-col items-center justify-between gap-4 md:flex-row">
                <div className="relative w-full">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-neutral-500" size={18} />
                    <input type="text" value={memberSearchTerm} onChange={(event) => setMemberSearchTerm(event.target.value)} placeholder={t('gdkd.detail.members.searchPlaceholder')} className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-10 pr-4 text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:placeholder:text-neutral-500" />
                </div>
                <div className="flex w-full flex-col gap-3 md:w-auto md:flex-row">
                    <select value={memberStatusFilter} onChange={(event) => setMemberStatusFilter(event.target.value as 'Tất cả trạng thái' | MemberPackageStatus)} className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-700 focus:border-blue-500 focus:outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 md:min-w-[180px] md:w-auto">
                        <option value="Tất cả trạng thái">{t('gdkd.detail.members.allStatuses')}</option>
                        <option value="Hoạt động">{t('gdkd.detail.members.statusActive')}</option>
                        <option value="Ngừng hoạt động">{t('gdkd.detail.members.statusInactive')}</option>
                        <option value="Hết thời gian ĐT">{t('gdkd.detail.members.statusExpired')}</option>
                    </select>
                    <select value={transferFilter} onChange={(event) => setTransferFilter(event.target.value as 'all' | 'is_transfer' | 'not_is_transfer')} className="w-full rounded-lg border border-gray-200 bg-white px-4 py-2 text-gray-700 focus:border-blue-500 focus:outline-none dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 md:min-w-[180px] md:w-auto">
                        <option value="all">{t('gdkd.detail.members.allTransfers')}</option>
                        <option value="is_transfer">{t('gdkd.detail.members.isTransfer')}</option>
                        <option value="not_is_transfer">{t('gdkd.detail.members.notTransfer')}</option>
                    </select>
                </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-gray-100 dark:border-neutral-800">
                <table className="min-w-[1000px] w-full whitespace-nowrap text-left text-sm">
                    <thead>
                        <tr className="border-b border-gray-100 bg-gray-50/50 dark:border-neutral-800 dark:bg-neutral-900/50">
                            <th className="px-4 py-4 text-xs font-semibold uppercase text-gray-500 dark:text-neutral-400">{t('gdkd.detail.members.table.member')}</th>
                            <th className="px-4 py-4 text-xs font-semibold uppercase text-gray-500 dark:text-neutral-400">{t('gdkd.detail.members.table.contact')}</th>
                            <th className="px-4 py-4 text-xs font-semibold uppercase text-gray-500 dark:text-neutral-400">{t('gdkd.detail.members.table.assignee')}</th>
                            <th className="px-4 py-4 text-xs font-semibold uppercase text-gray-500 dark:text-neutral-400">{t('gdkd.detail.members.table.package')}</th>
                            <th className="px-4 py-4 text-right text-xs font-semibold uppercase text-gray-500 dark:text-neutral-400">{t('gdkd.detail.members.table.totalInvestment')}</th>
                            <th className="px-4 py-4 text-center text-xs font-semibold uppercase text-gray-500 dark:text-neutral-400">{t('gdkd.detail.members.table.status')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-neutral-800">
                        {filteredMembers.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-4 py-10 text-center text-sm text-gray-500 dark:text-neutral-400">
                                    {t('gdkd.detail.members.noData')}
                                </td>
                            </tr>
                        ) : filteredMembers.map((member) => (
                            <tr key={`${member.name}-${member.email}`} className="hover:bg-gray-50/30 dark:hover:bg-neutral-800/40">
                                <td className="align-top px-4 py-4"><div className="font-semibold text-gray-700 dark:text-neutral-100">{member.name}</div><div className="mt-1 text-xs text-gray-400 dark:text-neutral-500">{member.idLabel}</div></td>
                                <td className="align-top px-4 py-4"><div className="mb-1 flex items-center gap-2 text-gray-600 dark:text-neutral-300"><Mail size={14} className="text-gray-400 dark:text-neutral-500" /><span>{member.email}</span></div><div className="flex items-center gap-2 text-xs text-gray-500 dark:text-neutral-400"><Phone size={14} className="text-gray-400 dark:text-neutral-500" /><span>{member.phone}</span></div></td>
                                <td className="align-top px-4 py-4"><div className="flex flex-col gap-4">{member.packages.map((pkg) => (<div key={`${member.name}-${pkg.name}-${pkg.transferDate}`} className="flex min-h-[82px] flex-col items-start justify-start"><div className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium ${pkg.isMine ? 'border-indigo-100 bg-indigo-50 text-indigo-700 dark:border-indigo-900/40 dark:bg-indigo-900/20 dark:text-indigo-300' : 'border-gray-200 bg-gray-50 text-gray-600 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-300'}`}><User size={12} />{pkg.assignee}</div><div className="mt-1.5 flex items-center gap-1 text-[11px] font-medium text-gray-500 dark:text-neutral-400"><CalendarDays size={10} className="text-gray-400 dark:text-neutral-500" />{ t('gdkd.detail.members.received') }: {pkg.transferDate}</div></div>))}</div></td>
                                <td className="align-top px-4 py-4"><div className="flex flex-col gap-4">{member.packages.map((pkg) => { const progress = getPackageProgress(pkg.startDate, pkg.duration); return (<div key={`${member.name}-${pkg.name}-${pkg.startDate}`} className="flex min-h-[82px] w-full max-w-[150px] flex-col justify-start"><div className="text-sm font-medium text-gray-700 dark:text-neutral-100">{pkg.name}</div>{progress ? (<div className="mb-1 mt-2 w-full"><div className="mb-1 h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-neutral-700"><div className={`h-1.5 rounded-full ${progress.isExpired ? 'bg-gray-400 dark:bg-neutral-500' : 'bg-blue-500 dark:bg-blue-400'}`} style={{ width: `${(progress.elapsed / progress.duration) * 100}%` }} /></div><div className="flex justify-between text-[10px] font-medium text-gray-500 dark:text-neutral-400"><span>Đã qua: <span className={progress.isExpired ? 'text-gray-500 dark:text-neutral-400' : 'text-blue-600 dark:text-blue-400'}>{progress.elapsed}T</span></span><span>Còn: {progress.remaining}T</span></div></div>) : null}</div>); })}</div></td>
                                <td className="align-top px-4 py-4 text-right font-medium text-gray-700 dark:text-neutral-100"><div className="flex flex-col gap-4">{member.packages.map((pkg) => (<div key={`${member.name}-${pkg.name}-${pkg.totalInvestment}`} className="flex min-h-[82px] items-start justify-end">{pkg.totalInvestment === '-' ? pkg.totalInvestment : formatCurrency(pkg.totalInvestment)}</div>))}</div></td>
                                <td className="align-top px-4 py-4 text-center"><div className="flex flex-col gap-4">{member.packages.map((pkg) => (<div key={`${member.name}-${pkg.name}-${pkg.status}`} className="flex min-h-[82px] flex-col items-center justify-start gap-1.5"><StatusBadge status={pkg.status} />{pkg.isTransferred ? <span className="rounded border border-orange-100 bg-orange-50 px-1.5 py-0.5 text-[10px] font-medium text-orange-600">{t('gdkd.detail.members.transferred')}</span> : null}</div>))}</div></td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {memberTotal > 0 ? (
                    <div className="flex justify-end px-4 py-4">
                        <Pagination
                            current={memberPage}
                            total={memberTotal}
                            pageSize={memberPageSize}
                            onChange={setMemberPage}
                            showSizeChanger={false}
                        />
                    </div>
                ) : null}
            </div>

            {isExportModalOpen ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6">
                    <div className="w-full max-w-3xl rounded-xl bg-white shadow-2xl dark:bg-neutral-900">
                        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-neutral-800">
                            <div>
                                <h2 className="text-lg font-semibold text-gray-900 dark:text-neutral-100">{t('gdkd.detail.exportModal.title')}</h2>
                                <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">{t('gdkd.detail.exportModal.subtitle')}</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button type="button" onClick={() => setSelectedExportColumns(DEFAULT_SELECTED_EXPORT_COLUMNS)} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800">{t('gdkd.detail.exportModal.selectAll')}</button>
                                <button type="button" onClick={() => setSelectedExportColumns([])} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800">{t('gdkd.detail.exportModal.deselect')}</button>
                                <button type="button" onClick={handleResetExportColumnLabels} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800">{t('gdkd.detail.exportModal.resetLabels')}</button>
                            </div>
                        </div>

                        <div className="space-y-4 px-6 py-5">
                            <div className="grid max-h-[420px] grid-cols-1 gap-3 overflow-y-auto pr-1 md:grid-cols-2">
                                {EXPORT_COLUMNS.map((column) => (
                                    <div key={column.key} className="rounded-lg border border-gray-200 p-4 dark:border-neutral-800 dark:bg-neutral-900/50">
                                        <label className="flex items-center gap-3 text-sm font-medium text-gray-800 dark:text-neutral-200">
                                            <input
                                                type="checkbox"
                                                checked={selectedExportColumns.includes(column.key)}
                                                onChange={() => handleToggleExportColumn(column.key)}
                                                className="h-4 w-4 rounded border-gray-300 dark:border-neutral-700"
                                            />
                                            <span>{column.label}</span>
                                        </label>
                                        <div className="mt-3">
                                            <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-neutral-400">{t('gdkd.detail.exportModal.columnNameLabel')}</label>
                                            <input
                                                type="text"
                                                value={exportColumnLabels[column.key]}
                                                onChange={(event) => handleExportColumnLabelChange(column.key, event.target.value)}
                                                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 outline-none transition-colors focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100"
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <p className="text-xs text-gray-500 dark:text-neutral-400">{t('gdkd.detail.exportModal.selectedCount', { count: selectedExportColumns.length, total: EXPORT_COLUMNS.length })}</p>
                        </div>

                        <div className="flex items-center justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-neutral-800">
                            <button type="button" onClick={() => setIsExportModalOpen(false)} className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-neutral-700 dark:text-neutral-300 dark:hover:bg-neutral-800">{t('gdkd.detail.exportModal.cancel')}</button>
                            <button type="button" onClick={handleExportMembersExcel} disabled={exporting} className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-gray-400">{exporting ? t('gdkd.detail.exportModal.exporting') : t('gdkd.detail.exportModal.export')}</button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );

    const renderPlaceholderTab = () => (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <LayoutDashboard size={48} className="mb-4 opacity-20" />
            <p>{t('gdkd.detail.placeholderTab')}</p>
        </div>
    );

    const renderTabContent = () => {
        switch (activeTab) {
            case 'member':
                return renderMemberTab();
            case 'investment':
                return <InvestmentTab stats={investmentStats} investments={investments} loading={loading} />;
            case 'transactions':
                return <TransactionsTab
                    transactions={transactions}
                    stats={transactionStats}
                    currentPage={transPage}
                    totalItems={transTotal}
                    onPageChange={setTransPage}
                />;
            case 'commission':
                return <MembersCommissionTab
                    stats={commissionData?.stats}
                    records={commissionData?.records}
                    currentPage={commPage}
                    totalItems={commTotal}
                    onPageChange={setCommPage}
                />;
            case 'contracts':
                return <DocumentsTab
                    userId={Number(profile?.id) || 0}
                    docs={docs}
                    currentPage={docPage}
                    totalItems={docTotal}
                    onPageChange={setDocPage}
                />;
            default:
                return renderPlaceholderTab();
        }
    };

    if (loading && !profile) {
        return (
            <div className="flex min-h-100 items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (!profile) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
                <h1 className="text-xl font-bold text-gray-800 dark:text-neutral-100">{t('gdkd.detail.notFound')}</h1>
                <p className="mt-2 text-sm text-gray-500 dark:text-neutral-400">{t('gdkd.detail.notFoundDesc')}</p>
                <button onClick={() => navigate('/admin/gdkd')} className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700">
                    {t('gdkd.detail.backToList')}
                </button>
            </div>
        );
    }

    return (
        <div className="dark:bg-neutral-950">
            <div className="mb-6 flex flex-col justify-between gap-4 md:flex-row md:items-center">
                <div/>
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-neutral-400">
                    <button onClick={() => navigate('/admin/dashboard')} className="flex items-center gap-1 transition-colors hover:text-blue-600 dark:hover:text-blue-400">
                        <LayoutDashboard size={14} />
                        <span>{t('gdkd.detail.dashboard')}</span>
                    </button>
                    <ChevronRight size={14} />
                    <button onClick={() => navigate('/admin/gdkd')} className="transition-colors hover:text-blue-600 dark:hover:text-blue-400">
                        {t('gdkd.detail.manageGdkd')}
                    </button>
                    <ChevronRight size={14} />
                    <span className="font-bold uppercase text-gray-800 dark:text-neutral-100">{profile.fullname}</span>
                </div>
            </div>

            <div className="flex flex-col gap-6 xl:flex-row">
                <div className="w-full flex-shrink-0 xl:w-80">
                    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
                        <div className="relative h-24 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/60 dark:to-indigo-950/40" />
                        <div className="relative z-10 mb-4 -mt-12 flex justify-center">
                            <div className="h-24 w-24 rounded-full bg-white p-1 shadow-sm">
                                <div className="flex h-full w-full items-center justify-center rounded-full bg-gray-100 text-gray-400 dark:bg-neutral-800 dark:text-neutral-500">
                                    <User size={40} />
                                </div>
                            </div>
                        </div>

                        <div className="px-6 pb-6">
                            <h2 className="mb-4 text-lg font-bold text-gray-800 dark:text-neutral-100">{t('gdkd.detail.personalInfo')}</h2>
                            <div className="space-y-4 text-sm">
                                <div className="grid grid-cols-[100px_1fr] gap-2"><span className="text-gray-500 dark:text-neutral-400">{t('gdkd.detail.fullName')}</span><span className="font-medium text-gray-800 dark:text-neutral-100">: {profile.fullname}</span></div>
                                <div className="grid grid-cols-[100px_1fr] gap-2"><span className="text-gray-500 dark:text-neutral-400">{t('gdkd.detail.email')}</span><span className="font-medium text-gray-800 dark:text-neutral-100">: {profile.email}</span></div>
                                <div className="grid grid-cols-[100px_1fr] gap-2"><span className="text-gray-500 dark:text-neutral-400">{t('gdkd.detail.phone')}</span><span className="font-medium text-gray-800 dark:text-neutral-100">: {profile.phone || '-'}</span></div>
                                <div className="grid grid-cols-[100px_1fr] gap-2"><span className="text-gray-500 dark:text-neutral-400">{t('gdkd.detail.gender')}</span><span className="font-medium text-gray-800 dark:text-neutral-100">: {typeof profile.gender === 'string' ? profile.gender : profile.gender === 1 ? t('gdkd.detail.male') : profile.gender === 2 ? t('gdkd.detail.female') : '-'}</span></div>
                                <div className="grid grid-cols-[100px_1fr] gap-2"><span className="text-gray-500 dark:text-neutral-400">{t('gdkd.detail.birthday')}</span><span className="font-medium text-gray-800 dark:text-neutral-100">: {profile.birthday || '-'}</span></div>
                                <div className="grid grid-cols-[100px_1fr] gap-2"><span className="text-gray-500 dark:text-neutral-400">{t('gdkd.detail.occupation')}</span><span className="font-medium text-gray-800 dark:text-neutral-100">: {profile.job_title || '-'}</span></div>
                                <div className="grid grid-cols-[100px_1fr] gap-2"><span className="text-gray-500 dark:text-neutral-400">{t('gdkd.detail.referralCode')}</span><span className="font-medium text-gray-800 dark:text-neutral-100">: {profile.referral_code || '-'}</span></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="flex flex-1 flex-col rounded-xl border border-gray-100 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
                    <div className="flex gap-6 border-b border-gray-100 px-6 pt-4 dark:border-neutral-800">
                        {tabs.map((tab) => (
                            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`pb-3 text-sm transition-colors ${activeTab === tab.id ? 'border-b-2 border-blue-600 font-bold text-blue-600 dark:text-blue-400' : 'font-medium text-gray-500 hover:text-gray-800 dark:text-neutral-400 dark:hover:text-neutral-100'}`}>
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div className="overflow-y-auto p-6">
                        {loading && activeTab !== 'member' ? (
                            <div className="flex justify-center p-10"><Spinner /></div>
                        ) : (
                            renderTabContent()
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}